import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2, Phone, Mail, MapPin, MessageCircle, Instagram, Facebook, Twitter, Linkedin, Youtube } from "lucide-react";
import { invalidateSiteContact } from "../hooks/useSiteContact";

const inputCls = "w-full bg-white border border-black/10 rounded-sm px-3 py-2 focus:ring-2 focus:ring-[var(--brand-primary)]/20 focus:border-[var(--brand-primary)] outline-none";

const EMPTY = {
  phones: [""],
  whatsapp: "",
  whatsapp_message: "",
  email: "",
  address: "",
  instagram_url: "",
  facebook_url: "",
  twitter_url: "",
  linkedin_url: "",
  youtube_url: "",
};

export default function AdminSiteContact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/site/contact")
      .then((r) => {
        const d = r.data || {};
        setForm({
          ...EMPTY,
          ...d,
          phones: (d.phones && d.phones.length ? d.phones : [""]),
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPhone = (i, v) => setForm((f) => ({ ...f, phones: f.phones.map((p, idx) => idx === i ? v : p) }));
  const addPhone = () => setForm((f) => ({ ...f, phones: [...f.phones, ""] }));
  const removePhone = (i) => setForm((f) => ({ ...f, phones: f.phones.filter((_, idx) => idx !== i) }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, phones: (form.phones || []).map((p) => p.trim()).filter(Boolean) };
      await api.put("/site/contact", payload);
      invalidateSiteContact();
      toast.success("Contact info updated — changes reflect across the site");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 lg:p-10 text-sm text-[#525252]">Loading…</div>;

  return (
    <form onSubmit={save} data-testid="admin-site-contact-page" className="p-6 lg:p-10 space-y-8 max-w-3xl">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-[#525252] mb-1">Site content</div>
        <h1 className="font-display text-3xl font-black inline-flex items-center gap-2"><Phone size={24}/> Contact info</h1>
        <p className="text-sm text-[#525252] mt-1 max-w-xl">
          Shown in the site footer, WhatsApp floating button, and Contact page. Changes reflect across every page after saving.
        </p>
      </div>

      <Section title="Phones">
        <div className="space-y-2">
          {form.phones.map((p, i) => (
            <div key={i} className="flex gap-2">
              <input type="tel" value={p} onChange={(e) => setPhone(i, e.target.value)}
                     data-testid={`contact-phone-${i}`}
                     placeholder="+91 98765 43210" className={inputCls}/>
              {form.phones.length > 1 && (
                <button type="button" onClick={() => removePhone(i)} data-testid={`contact-phone-remove-${i}`}
                        className="px-2 hover:bg-black/5 rounded-sm"><Trash2 size={16}/></button>
              )}
            </div>
          ))}
          {form.phones.length < 5 && (
            <button type="button" onClick={addPhone} data-testid="contact-phone-add"
                    className="text-sm text-[var(--brand-primary)] inline-flex items-center gap-1 hover:underline">
              <Plus size={14}/> Add another phone
            </button>
          )}
        </div>
      </Section>

      <Section title="WhatsApp">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="WhatsApp number (digits only, incl. country code)" icon={MessageCircle}>
            <input type="tel" value={form.whatsapp} onChange={(e) => setField("whatsapp", e.target.value)}
                   data-testid="contact-whatsapp"
                   placeholder="918679955515" className={inputCls}/>
          </Field>
          <Field label="Default WhatsApp message" icon={MessageCircle}>
            <input type="text" value={form.whatsapp_message}
                   onChange={(e) => setField("whatsapp_message", e.target.value)}
                   data-testid="contact-whatsapp-message"
                   placeholder="Hi RestroSetup!" className={inputCls}/>
          </Field>
        </div>
      </Section>

      <Section title="Email & address">
        <div className="space-y-4">
          <Field label="Email" icon={Mail}>
            <input type="email" value={form.email} onChange={(e) => setField("email", e.target.value)}
                   data-testid="contact-email" className={inputCls}/>
          </Field>
          <Field label="Address (optional)" icon={MapPin}>
            <textarea value={form.address} onChange={(e) => setField("address", e.target.value)}
                      data-testid="contact-address" rows={2} className={inputCls}/>
          </Field>
        </div>
      </Section>

      <Section title="Social links (optional)">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { key: "instagram_url", label: "Instagram", icon: Instagram },
            { key: "facebook_url",  label: "Facebook",  icon: Facebook },
            { key: "twitter_url",   label: "Twitter / X", icon: Twitter },
            { key: "linkedin_url",  label: "LinkedIn",  icon: Linkedin },
            { key: "youtube_url",   label: "YouTube",   icon: Youtube },
          ].map(({ key, label, icon }) => (
            <Field key={key} label={label} icon={icon}>
              <input type="url" value={form[key]} onChange={(e) => setField(key, e.target.value)}
                     data-testid={`contact-${key}`}
                     placeholder="https://..." className={inputCls}/>
            </Field>
          ))}
        </div>
      </Section>

      <div className="flex items-center gap-3 pt-4">
        <button type="submit" disabled={saving} data-testid="save-contact-button"
                className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-60 text-white px-5 py-2 rounded-sm font-medium inline-flex items-center gap-2">
          <Save size={16}/> {saving ? "Saving…" : "Save changes"}
        </button>
        <span className="text-xs text-[#525252]">Public site + Contact page will refresh automatically.</span>
      </div>
    </form>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-black/10 rounded-sm bg-white p-5">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#525252] mb-4">{title}</div>
      {children}
    </div>
  );
}
function Field({ label, icon: Icon, children }) {
  return (
    <label className="block">
      <div className="text-sm font-medium mb-2 inline-flex items-center gap-1.5">
        {Icon && <Icon size={12} className="text-[#525252]"/>} {label}
      </div>
      {children}
    </label>
  );
}
